import { useState } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';

const GuarantorForm = () => {
  const { userId } = useParams();
  const [formData, setFormData] = useState({
    title: '',
    firstName: '',
    previousName: '',
    residentialAddress: '',
    timeAtAddress: '',
    mobile: '',
    email: '',
    dateOfBirth: '',
    relationship: '',
    previousOccupation: '',
    currentOccupation: '',
    lineManager: '',
    dateStarted: '',
    jobTitle: '',
    previousOccupationDetails: '',
    oath: false,
    declaration: false,
    accountName: '',
    accountNumber: '',
    bankName: '',
    passport: '',
    signature: ''
  });
  const [loading, setLoading] = useState(false);
  const [notification, setNotification] = useState('');
  const [error, setError] = useState('');
  const [passportPreview, setPassportPreview] = useState('');
  const [signaturePreview, setSignaturePreview] = useState('');

  const CLOUDINARY_API_KEY = process.env.REACT_APP_CLOUDINARY_API_KEY || '359245968887242';
  const CLOUDINARY_PRESET = process.env.REACT_APP_CLOUDINARY_PRESET || 'oosh_preset';
  const CLOUDINARY_NAME = process.env.REACT_APP_CLOUDINARY_NAME || 'digzrkdoe';

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
    setError(''); // Clear error on input change
  };

  const handleFileChange = async (e, field) => {
    const file = e.target.files[0];
    if (!file) {
      setError(`Please select a ${field} image`);
      return;
    }

    // Validate file type and size
    const validTypes = ['image/jpeg', 'image/png', 'image/gif'];
    if (!validTypes.includes(file.type)) {
      setError(`Invalid file type for ${field}. Please upload a JPEG, PNG, or GIF image.`);
      return;
    }
    if (file.size > 5 * 1024 * 1024) { // 5MB limit
      setError(`File size for ${field} exceeds 5MB.`);
      return;
    }

    setLoading(true);
    setError('');
    setNotification('');

    const form = new FormData();
    form.append('file', file);
    form.append('upload_preset', CLOUDINARY_PRESET);

    try {
      const response = await axios.post(
        `https://api.cloudinary.com/v1_1/${CLOUDINARY_NAME}/image/upload`,
        form,
        {
          headers: { 'Content-Type': 'multipart/form-data' }
        }
      );
      const url = response.data.secure_url;
      setFormData(prev => ({
        ...prev,
        [field]: url
      }));
      if (field === 'passport') setPassportPreview(url);
      if (field === 'signature') setSignaturePreview(url);
      setNotification(`${field.charAt(0).toUpperCase() + field.slice(1)} uploaded successfully`);
    } catch (error) {
      console.error(`Cloudinary ${field} upload error:`, error.response?.data || error.message);
      setError(`Failed to upload ${field}. Please try again.`);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setNotification('');

    // Validate required fields
    if (!formData.passport) {
      setError('Passport image is required');
      return;
    }
    if (!formData.signature) {
      setError('Signature image is required');
      return;
    }

    setLoading(true);

    try {
      const response = await axios.post(`${process.env.REACT_APP_API_URL}guarantor/${userId}`, formData);
      if (response.data.success) {
        setNotification('Form submitted successfully');
        setFormData({
          title: '',
          firstName: '',
          previousName: '',
          residentialAddress: '',
          timeAtAddress: '',
          mobile: '',
          email: '',
          dateOfBirth: '',
          relationship: '',
          previousOccupation: '',
          currentOccupation: '',
          lineManager: '',
          dateStarted: '',
          jobTitle: '',
          previousOccupationDetails: '',
          oath: false,
          declaration: false,
          accountName: '',
          accountNumber: '',
          bankName: '',
          passport: '',
          signature: ''
        });
        setPassportPreview('');
        setSignaturePreview('');
      } else {
        setError('Error submitting form');
      }
    } catch (error) {
      console.error('Submit error:', error.response?.data || error.message);
      setError(`Error submitting form: ${error.response?.data?.message || error.message}`);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="container mx-auto p-4 max-w-4xl">
      <h1 className="text-2xl font-bold mb-4 text-center">Guarantor Form</h1>

      {error && <p className="mb-4 text-red-600">{error}</p>}
      {notification && <p className="mb-4 text-green-600">{notification}</p>}

      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Passport and Signature Upload */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700">Passport</label>
            <input
              type="file"
              onChange={(e) => handleFileChange(e, 'passport')}
              className="mt-1 block w-full border p-2 rounded"
              accept="image/jpeg,image/png,image/gif"
              disabled={loading}
            />
            {passportPreview && <img src={passportPreview} alt="Passport Preview" className="mt-2 w-24 h-24 object-cover rounded" />}
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700">Signature</label>
            <input
              type="file"
              onChange={(e) => handleFileChange(e, 'signature')}
              className="mt-1 block w-full border p-2 rounded"
              accept="image/jpeg,image/png,image/gif"
              disabled={loading}
            />
            {signaturePreview && <img src={signaturePreview} alt="Signature Preview" className="mt-2 w-24 h-24 object-cover rounded" />}
          </div>
        </div>

        {/* Personal Information */}
        <h2 className="text-xl font-semibold mb-2">GUARANTOR 1 - Personal Information</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700">Title</label>
            <input
              type="text"
              name="title"
              value={formData.title}
              onChange={handleChange}
              className="mt-1 block w-full border p-2 rounded"
              required
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700">First Name</label>
            <input
              type="text"
              name="firstName"
              value={formData.firstName}
              onChange={handleChange}
              className="mt-1 block w-full border p-2 rounded"
              required
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700">Any Previous Name in Full</label>
            <input
              type="text"
              name="previousName"
              value={formData.previousName}
              onChange={handleChange}
              className="mt-1 block w-full border p-2 rounded"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700">Current Residential Address</label>
            <input
              type="text"
              name="residentialAddress"
              value={formData.residentialAddress}
              onChange={handleChange}
              className="mt-1 block w-full border p-2 rounded"
              required
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700">Time Spent at This Address to Date</label>
            <input
              type="text"
              name="timeAtAddress"
              value={formData.timeAtAddress}
              onChange={handleChange}
              className="mt-1 block w-full border p-2 rounded"
              required
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700">Mobile</label>
            <input
              type="tel"
              name="mobile"
              value={formData.mobile}
              onChange={handleChange}
              className="mt-1 block w-full border p-2 rounded"
              required
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700">Email Address</label>
            <input
              type="email"
              name="email"
              value={formData.email}
              onChange={handleChange}
              className="mt-1 block w-full border p-2 rounded"
              required
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700">Date of Birth</label>
            <input
              type="date"
              name="dateOfBirth"
              value={formData.dateOfBirth}
              onChange={handleChange}
              className="mt-1 block w-full border p-2 rounded"
              required
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700">Relationship to Applicant</label>
            <input
              type="text"
              name="relationship"
              value={formData.relationship}
              onChange={handleChange}
              className="mt-1 block w-full border p-2 rounded"
              required
            />
          </div>
        </div>

        {/* Occupation Details */}
        <h2 className="text-xl font-semibold mb-2 mt-6">Occupation Details</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700">Present Occupation</label>
            <select
              name="currentOccupation"
              value={formData.currentOccupation}
              onChange={handleChange}
              className="mt-1 block w-full border p-2 rounded"
              required
            >
              <option value="">Select...</option>
              <option value="Employed">Employed</option>
              <option value="Self Employed">Self Employed</option>
              <option value="On contract">On contract</option>
              <option value="Retired">Retired</option>
              <option value="Unemployment">Unemployment</option>
              <option value="Student">Student</option>
              <option value="Independent Means">Independent Means</option>
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700">Line Manager or Other Contracts</label>
            <input
              type="text"
              name="lineManager"
              value={formData.lineManager}
              onChange={handleChange}
              className="mt-1 block w-full border p-2 rounded"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700">Date Started This Employment</label>
            <input
              type="date"
              name="dateStarted"
              value={formData.dateStarted}
              onChange={handleChange}
              className="mt-1 block w-full border p-2 rounded"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700">Current Job/Title</label>
            <input
              type="text"
              name="jobTitle"
              value={formData.jobTitle}
              onChange={handleChange}
              className="mt-1 block w-full border p-2 rounded"
            />
          </div>
          <div className="col-span-2">
            <label className="block text-sm font-medium text-gray-700">Previous Occupation (if less than 3 years in current job)</label>
            <textarea
              name="previousOccupation"
              value={formData.previousOccupation}
              onChange={handleChange}
              className="mt-1 block w-full border p-2 rounded"
              rows="3"
            />
          </div>
        </div>

        {/* OATH OF CONFIDENTIALITY */}
        <h2 className="text-xl font-semibold mb-2 mt-6">OATH OF CONFIDENTIALITY BY GUARANTOR 1</h2>
        <p className="text-sm text-gray-600 mb-4">
          I am acting as a guarantor for: (Full name of the forthcoming employee as stated on employment data form)
          In respect of the forthcoming employee commitments and fidelity. I believe the information provided here to be true and authorize essential.ng:
          Carry out verification or reference checks from any parties mentioned herein.
          Use the information obtained with third parties to assess suitability as guarantor and make decision on candidate's employment or confirmation.
          Handle all information obtained in strictest confidence.
          I understand that if I default on my guarantor's obligation, the information provided in this form may be used released to law enforcement agencies, law court or essential.ng clients to whom the employee is deployed.
          Accept that if the applicant didn't tender two (2) weeks resignation notification letter before leaving the company (quitting), I shall be responsible to pay the company sum of #30,000 being cost for urgent job agency/consultant charge fee regardless of how many weeks he/she has worked /done while expecting salary due and the fact that the company shall take up legal case against me for the amount claims and all legal cost claims.
          This form is true and I can vouch for the character and integrity................. 
        </p>
        <div className="flex items-center">
          <input
            type="checkbox"
            name="oath"
            checked={formData.oath}
            onChange={handleChange}
            className="mr-2"
            required
          />
          <label className="text-sm font-medium text-gray-700">I accept the Oath of Confidentiality</label>
        </div>

        {/* Declaration */}
        <h2 className="text-xl font-semibold mb-2 mt-6">Declaration</h2>
        <p className="text-sm text-gray-600 mb-4">
          Do not give false information about yourself and your guarantor.
          You are to give two (2) weeks' notice to the management in case of disengagement from service. Failure to give the said notice, one month salary and all entitlement shall be forfeited. If there's no two weeks resignation notification letter tendered, you must pay the company sum of #30,000 being cost for urgent job agency/consultant charge fees which would be paid by you otherwise your guarantor would be liable for the payment
          You should also note that you may be asked to go to any of Lagos or Ibadan branch at any time temporarily.
          The dress code is white and Black Trouser/Skirt with a Black long tie for men and black shoes.
          Iso notes that lateness attracts #300 each time while 3 times is #500C salaries are paid every 3 of the following month
          It is a criminal offence to make a false statement on this guarantor's form
          Kindly fill your account details here for any compensation or staff salary pay off
          Also if a staff is allegedly caught stealing such staff shall be criminally liable.
        </p>
        <div className="flex items-center">
          <input
            type="checkbox"
            name="declaration"
            checked={formData.declaration}
            onChange={handleChange}
            className="mr-2"
            required
          />
          <label className="text-sm font-medium text-gray-700">I accept the Declaration</label>
        </div>

        {/* Account Details */}
        <h2 className="text-xl font-semibold mb-2 mt-6">Account Details</h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700">Account Name</label>
            <input
              type="text"
              name="accountName"
              value={formData.accountName}
              onChange={handleChange}
              className="mt-1 block w-full border p-2 rounded"
              required
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700">Account Number</label>
            <input
              type="text"
              name="accountNumber"
              value={formData.accountNumber}
              onChange={handleChange}
              className="mt-1 block w-full border p-2 rounded"
              required
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700">Bank Name</label>
            <input
              type="text"
              name="bankName"
              value={formData.bankName}
              onChange={handleChange}
              className="mt-1 block w-full border p-2 rounded"
              required
            />
          </div>
        </div>

        {/* Submit Button */}
        <button
          type="submit"
          disabled={loading}
          className="mt-6 bg-blue-500 text-white p-2 rounded hover:bg-blue-600 disabled:bg-gray-500"
        >
          {loading ? (
            <div className="flex items-center justify-center">
              <div className="animate-spin rounded-full h-5 w-5 border-t-2 border-white mr-2"></div>
              Submitting...
            </div>
          ) : (
            'Submit'
          )}
        </button>
      </form>
    </div>
  );
};

export default GuarantorForm;